import type { Column, Row, RowSelectionState } from '@tanstack/react-table';
import type {
  AggregateFunctionName,
  CurrencyFormatOptions,
} from 'components/datatable/datatable.types';
import type { Currency } from 'types/account';
import type { Money } from 'types/money';
import { customFormatCurrency, formatCurrency } from './currency';
import { formattedDuration } from './durations';
import { pluralize } from './text-helper';

function formatMoney(
  value: number,
  currency: Currency | undefined,
  prefix: string,
  currencyFormat: CurrencyFormatOptions
): string {
  if (value === null || value === 0 || currency === undefined) {
    return '';
  }

  const formattedCurrency = currencyFormat
    ? customFormatCurrency(value, currency, currencyFormat)
    : formatCurrency(currency, value, 2);

  return `${prefix}. ${formattedCurrency}`;
}

function sumMoney(
  rows: Row<any>[],
  column: Column<any>,
  meta: any,
  rowSelection: RowSelectionState,
  average: boolean,
  prefix: string
): string {
  let sum = 0;
  let billableCount = 0;
  const currencies: Set<Currency> = new Set();
  const selectRows = Object.keys(rowSelection).length;
  rows.forEach(row => {
    if (selectRows > 0 && !row.getIsSelected()) {
      return;
    }

    if (row.original.billable === '-') {
      return;
    }

    const cell = row.getVisibleCells().find(cell => cell.column.id === column.id);
    if (cell) {
      const cellValue = cell.getValue() as Money;
      if (cellValue?.value == null) return;
      sum += cellValue.value;
      billableCount++;
      currencies.add(cellValue.currency);
    }
  });

  if (sum === 0 || currencies.size > 1) {
    return '';
  }
  if (average) {
    if (billableCount === 0) return '';
    return formatMoney(
      sum / billableCount,
      currencies.values().next().value,
      prefix,
      meta.currencyFormat
    );
  }

  return formatMoney(sum, currencies.values().next().value, prefix, meta.currencyFormat);
}

function sumNumeric(
  rows: Row<any>[],
  column: Column<any>,
  rowSelection: RowSelectionState,
  meta: any
): string {
  let sum = 0;
  const selectRows = Object.keys(rowSelection).length;

  rows.forEach(row => {
    if (selectRows > 0 && !row.getIsSelected()) {
      return;
    }

    const cell = row.getVisibleCells().find(cell => cell.column.id === column.id);
    if (cell) {
      const cellValue =
        meta.getValue === undefined ? cell.getValue() : meta.getValue(cell.getValue());
      sum += parseFloat(cellValue);
    }
  });
  return sum.toString();
}

function sumTime(
  rows: Row<any>[],
  column: Column<any>,
  rowSelection: RowSelectionState,
  meta: any,
  average: boolean,
  prefix: string
): string {
  const sum = Number(sumNumeric(rows, column, rowSelection, meta));

  if (sum === 0) {
    return '';
  }

  if (average) {
    const length =
      Object.keys(rowSelection).length > 0 ? Object.keys(rowSelection).length : rows.length;
    return `${prefix}. ${formattedDuration(Math.trunc(sum / length), meta.timeStyle)}`;
  }

  return `${prefix}. ${formattedDuration(sum, meta.timeStyle)}`;
}

function sum(rows: Row<any>[], column: Column<any>, rowSelection: RowSelectionState): string {
  return sumWithAverage(rows, column, rowSelection, false, 'Sum');
}

function sumWithAverage(
  rows: Row<any>[],
  column: Column<any>,
  rowSelection: RowSelectionState,
  average: boolean,
  prefix: string
): string {
  const meta: any = column.columnDef.meta;
  if (!meta.numeric) {
    return '0';
  }

  if (meta.money) {
    return sumMoney(rows, column, meta, rowSelection, average, prefix);
  } else if (meta.time) {
    return sumTime(rows, column, rowSelection, meta, average, prefix);
  } else if (meta.numeric) {
    if (prefix === 'Sum') {
      return `${prefix} ${sumNumeric(rows, column, rowSelection, meta)}`;
    }
    return `${prefix}. ${sumNumeric(rows, column, rowSelection, meta)}`;
  } else {
    return '0';
  }
}

function count(
  rows: Row<any>[],
  column: Column<any>,
  rowSelection: RowSelectionState,
  isPercent: boolean
): string {
  let validEntries = 0;
  const meta: any = column.columnDef.meta;
  const selectRows = Object.keys(rowSelection).length;
  rows.forEach(row => {
    if (selectRows > 0 && !row.getIsSelected()) {
      return;
    }

    const cell = row.getVisibleCells().find(cell => cell.column.id === column.id);
    if (cell) {
      const cellValue =
        meta.getValue === undefined ? cell.getValue() : meta.getValue(cell.getValue());

      if (Array.isArray(cellValue)) {
        const values = cellValue as string[];
        if (values?.length !== 0) {
          // For aggregate percent view, we need to consider column with multiple values, like tags, as one entry
          // Hence, we need to consider the array length as one valid entry count for the percentage calculation
          validEntries += isPercent ? 1 : values.length;
        }
      } else if (cellValue !== '') {
        validEntries += 1;
      }
    }
  });

  return validEntries.toString();
}

function countPercentage(
  rows: Row<any>[],
  column: Column<any>,
  rowSelection: RowSelectionState
): string {
  const validEntries = Number(count(rows, column, rowSelection, true));
  const selectRows = Object.keys(rowSelection).length;
  const totalEntries = selectRows > 0 ? selectRows : rows.length;

  return Math.trunc((validEntries / totalEntries) * 100) + '%';
}

function average(rows: any[], column: Column<any>, rowSelection: RowSelectionState): string {
  return sumWithAverage(rows, column, rowSelection, true, 'Avg');
}

function uniqueCount(
  rows: Row<any>[],
  column: Column<any>,
  rowSelection: RowSelectionState
): string {
  const entries = new Set<string>();
  const meta: any = column.columnDef.meta || {};
  const selectRows = Object.keys(rowSelection).length;

  rows.forEach(row => {
    if (selectRows > 0 && !row.getIsSelected()) {
      return;
    }

    const cell = row.getVisibleCells().find(cell => cell.column.id === column.id);
    if (cell) {
      const cellValue =
        meta.getValue === undefined ? cell.getValue() : meta.getValue(cell.getValue());

      if (meta.money) {
        const parsedMoney = cellValue as Money | undefined;

        if (parsedMoney?.value == null || parsedMoney.value === 0) {
          return;
        } else {
          entries.add(parsedMoney.value.toString());
        }
      } else if (meta.linkedWork && Array.isArray(cellValue)) {
        // Special handling for linked work columns
        const linkedWorkItems = cellValue as Array<any>;
        const field = meta.linkedWorkField || 'external_code';
        const index = meta.linkedWorkIndex;

        if (index !== undefined) {
          // For indexed linked work columns, only get the value at that specific index
          const item = linkedWorkItems[index];
          const value = item?.[field];
          if (value != null && String(value).trim() !== '') {
            entries.add(String(value).trim());
          }
        } else {
          // For non-indexed columns, get all values
          linkedWorkItems.forEach(item => {
            const value = item?.[field];
            if (value != null && String(value).trim() !== '') {
              entries.add(String(value).trim());
            }
          });
        }
      } else if (Array.isArray(cellValue)) {
        const values = cellValue as string[];
        values.forEach(value => {
          // Filter out empty, null, undefined values
          if (value != null && String(value).trim() !== '') {
            entries.add(value);
          }
        });
      } else if (cellValue != null && cellValue !== '' && (!meta.time || cellValue !== 0)) {
        entries.add(cellValue);
      }
    }
  });

  // Return empty for linked work columns with no data
  if (meta.linkedWork && entries.size === 0) {
    return '';
  }

  if ((meta.money || meta.time) && entries.size === 0) {
    return '';
  }
  if (meta && meta.numeric) {
    return `${entries.size}`;
  } else {
    const labelPlural = meta && meta.labelPlural ? meta.labelPlural : undefined;
    if (labelPlural) {
      // For linked work and other columns with custom plural, derive singular form
      let singular = labelPlural;
      if (labelPlural.endsWith('ses')) {
        // "Statuses" -> "Status", "Addresses" -> "Address"
        singular = labelPlural.slice(0, -2);
      } else if (labelPlural.endsWith('ies')) {
        // "Categories" -> "Category"
        singular = labelPlural.slice(0, -3) + 'y';
      } else if (labelPlural.endsWith('s')) {
        // "IDs" -> "ID", "Titles" -> "Title"
        singular = labelPlural.slice(0, -1);
      }
      return pluralize(entries.size, {
        singular: singular,
        plural: labelPlural,
      });
    }
    const label = meta && meta.label ? meta.label : column.columnDef.id;
    return pluralize(entries.size, {
      singular: label,
      plural: `${label}s`,
    });
  }
}

function median(rows: Row<any>[], column: Column<any>, rowSelection: RowSelectionState): string {
  const values: number[] = [];
  const currencies: Set<Currency> = new Set();
  const meta: any = column.columnDef.meta;
  const selectRows = Object.keys(rowSelection).length;

  rows.forEach(row => {
    if (selectRows > 0 && !row.getIsSelected()) {
      return;
    }

    const cell = row.getVisibleCells().find(cell => cell.column.id === column.id);
    if (cell) {
      const cellValue =
        meta.getValue === undefined ? cell.getValue() : meta.getValue(cell.getValue());
      if (cellValue !== '') {
        let parsedValue: number;
        if (meta.money) {
          const parsedMoney = cellValue as Money;
          parsedValue = parsedMoney.value;
          currencies.add(parsedMoney.currency);
        } else {
          parsedValue = parseFloat(cellValue);
        }
        if (!isNaN(parsedValue)) {
          values.push(parsedValue);
        }
      }
    }
  });

  if (values.length === 0) return '0';

  values.sort((a, b) => a - b);
  const mid = Math.floor(values.length / 2);

  const median = values.length % 2 !== 0 ? values[mid] : (values[mid - 1] + values[mid]) / 2;

  if (median === 0 || currencies.size > 1) {
    return '';
  }

  if (meta.money) {
    return formatMoney(median, currencies.values().next().value, 'Med', meta.currencyFormat);
  } else if (meta.time) {
    return `Med. ${formattedDuration(median, meta.timeStyle)}`;
  } else {
    return `Med. ${median}`;
  }
}

function emptyLabel(): string {
  return 'Select';
}

const aggregateFunctions: Record<AggregateFunctionName, any> = {
  average,
  count,
  countPercentage,
  sum,
  median,
  uniqueCount,
  emptyLabel,
};

export default aggregateFunctions;
